import { Dropdown, DropdownItemProps, StrictDropdownProps } from 'semantic-ui-react';
import React from 'react';
import {
  UserRole,
  EditUserInput,
  useTeamUsersQuery,
  UserOrderByFields,
  OrderDirection,
  MemberFragment,
  MediaRefFragment,
} from '$/GraphQL/schema/schema';
import CirclePicture from '$/components/ui/CirclePicture';
import UserEditModal from '$/sections/Home/Profile/TeamManager/UserEditModal';
import { Portal } from '$/components/Portal';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';
import { hasPermission } from '$/utils/hasPermission';
import { usePermissionModal } from '$/components/Permission';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { theme } from '$/components/Layout/theme';
import { useMe } from '$/hooks/useMe';
import Button from '../Button';

export interface UserOption {
  value: string;
  name?: string;
  email: string;
  profileMediaRef?: MediaRefFragment | null;
  role?: string | null;
}

type Props = Omit<StrictDropdownProps, 'onChange'> & {
  hideProfilePhoto?: boolean;
  value?: string;
  className?: string;
  placeholder?: string;
  defaultRole?: UserRole;
  allowNone?: string | boolean;
  filterOut?: string[];
  isActive?: boolean;
  centered?: boolean;
  color?: string;
  hideEmail?: boolean;
  enableEdit?: boolean;
  useDisplayEmail?: boolean;
  onChange?: (id: string) => void;
  userFilter?: (user: MemberFragment) => boolean;
};

const UserDropdown: React.FC<Props> = ({
  hideProfilePhoto,
  value,
  onChange,
  defaultRole = UserRole.Moderator,
  userFilter,
  hideEmail,
  placeholder = 'Select User',
  allowNone,
  className,
  filterOut,
  isActive,
  centered,
  color = theme.colors.color.brand,
  enableEdit = true,
  useDisplayEmail,
  ...props
}) => {
  const me = useMe();
  const { showPermissionModal } = usePermissionModal();
  const [editUserModal, setEditUserModal] = React.useState<Partial<EditUserInput> | null>(null);
  const variables = {
    filters: {
      orderBy: UserOrderByFields.FirstName,
      orderDirection: OrderDirection.Asc,
    },
  };
  const usersQuery = useTeamUsersQuery({ variables });

  const users = React.useMemo(() => {
    let users = usersQuery.data?.teamUsers || [];

    if (userFilter) {
      users = users.filter(userFilter);
    }
    if (isActive) {
      users = users.filter((u) => u.isVerified);
    }

    users = [...users].sort((a, b) => a.firstName.localeCompare(b.firstName));
    return users;
  }, [isActive, userFilter, usersQuery.data?.teamUsers]);

  const closeUserModal = async (id?: string): Promise<void> => {
    if (typeof usersQuery !== 'undefined') {
      await usersQuery.refetch({ ...variables });
    }

    if (id) {
      onChange?.(id);
    }

    setEditUserModal(null);
  };

  const openUserModal = (option?: UserOption | null): void => {
    const user = users.filter((u) => u.id === option?.value)[0] ?? {};
    setEditUserModal(user as EditUserInput);
  };

  const options: UserOption[] = [];

  if (allowNone) {
    options.push({
      value: '',
      name: typeof allowNone === 'string' ? allowNone : 'None',
      email: '',
      profileMediaRef: null,
      role: null,
    });
  }

  users.map((u) => {
    if (filterOut?.includes(u.id)) return;
    const email = u.role === UserRole.Presenter ? u.displayEmail ?? '' : u.email;
    options.push({
      value: u.id,
      name: [u.firstName, u.lastName].join(' '),
      email: useDisplayEmail ? u.displayEmail ?? '' : email,
      profileMediaRef: u.profileMediaRef,
      role: u.role,
    });
  });

  let currentOption = options.find((o) => o.value === value);

  if (!currentOption) {
    currentOption = {
      value: '-',
      email: '',
      name: allowNone ? 'None' : placeholder,
    };
  }

  const DropdownUserItem: React.FC<{
    hideProfilePhoto?: boolean;
    option?: UserOption;
    className?: string;
    selected?: boolean;
  }> = ({ hideProfilePhoto, option, className, selected }) => {
    if (!option) {
      option = {
        value: '',
        email: '',
        name: 'None',
      };
    }

    const showEdit =
      currentOption!.value === option.value && option.value !== '' && option.value !== '-';

    return (
      <div className={`flex font-body w-full justify-between items-center ${className}`}>
        <div
          className='option-name'
          css={css`
            ${tw`flex items-center text-md text-color-input-field`}
            flex-grow: 1;
            ${centered &&
            `
              ${selected ? `padding-left: ${showEdit ? '4rem' : '1.5rem'};` : ''}
              justify-content: center;
              width: 100%;
            `}
          `}
        >
          {!hideProfilePhoto && option.value && (
            <CirclePicture className='ProfileImage mr-3' pictureRef={option?.profileMediaRef} />
          )}
          <div className='flex flex-col'>
            <div>{option.name || 'None'}</div>
            {!hideEmail && (
              <div className='text-sm text-gray-1 user-dropdown-email'>{option.email}</div>
            )}
          </div>
        </div>

        {showEdit && enableEdit && (
          <Button.row
            className='edit-button mr-2'
            onClick={(e): void => {
              e.preventDefault();
              e.stopPropagation();
              if (!hasPermission(rolesPermissionsMatrix.profileTeam.path, me)) {
                showPermissionModal && showPermissionModal(rolesPermissionsMatrix.profileTeam.path);
              } else {
                openUserModal(option);
              }
            }}
          >
            Edit
          </Button.row>
        )}

        {!enableEdit && option.role && <span>{option.role}</span>}
      </div>
    );
  };

  const dropdownClass =
    'flex justify-between items-center w-full whitespace-nowrap border border-solid border-color-input-box rounded-input shadow-none p-3';
  const dropdownSelectedClass =
    'dropdown-selected flex justify-between items-center w-full max-h-100vh';

  return (
    <div className={`flex items-center ${className}`}>
      {editUserModal && (
        <Portal containerId='modalContainer'>
          <UserEditModal
            mode={editUserModal.id ? 'edit' : 'create'}
            onClose={closeUserModal}
            defaultValues={editUserModal ? editUserModal : { role: defaultRole }}
          />
        </Portal>
      )}

      {editUserModal ? (
        <div className={`ui dropdown ${dropdownClass}`}>
          <div className={dropdownSelectedClass}>
            <DropdownUserItem
              selected
              hideProfilePhoto={Boolean(hideProfilePhoto)}
              option={currentOption}
            />
          </div>
          <i aria-hidden='true' className='dropdown icon' />
        </div>
      ) : (
        <Dropdown
          {...props}
          className={`${dropdownClass} ${className}`}
          trigger={
            <div className={dropdownSelectedClass}>
              <DropdownUserItem
                selected
                hideProfilePhoto={Boolean(hideProfilePhoto)}
                option={currentOption}
              />
            </div>
          }
        >
          <Dropdown.Menu className='w-full max-h-45vh overflow-auto'>
            {enableEdit && (
              <Dropdown.Item
                value='-1'
                onClick={(): void => {
                  if (!hasPermission(rolesPermissionsMatrix.profileTeam.path, me)) {
                    showPermissionModal &&
                      showPermissionModal(rolesPermissionsMatrix.profileTeam.path);
                  } else {
                    setEditUserModal({ role: defaultRole });
                  }
                }}
                css={tw`flex`}
                content={
                  <div
                    className='add-new text-color-brand'
                    css={css`
                      ${tw`border border-color-buttonBorder rounded-sm bg-white py-1/4e text-center cursor-pointer font-body`}
                    `}
                  >
                    Add someone new
                  </div>
                }
              />
            )}
            {options.map((option: UserOption) =>
              option.value === value ? null : (
                <Dropdown.Item
                  key={option.value}
                  value={option.value}
                  className={`flex w-full items-center text-md text-color-input-field ${
                    option.value === value ? 'font-semibold' : ''
                  }`}
                  onClick={(_e, option: DropdownItemProps): void => {
                    onChange?.(option.value as string);
                  }}
                  content={<DropdownUserItem hideProfilePhoto={hideProfilePhoto} option={option} />}
                />
              )
            )}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
};

export default UserDropdown;
